import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight, Check } from 'lucide-react';
import { getIcon } from '../data/iconMap';
import { CATEGORIES, CATEGORY_ORDER, getCategory } from '../data/categories';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Create Listing flow.
 * Step 1: choose top-level category (icon grid).
 * Step 2: choose subcategory.
 * Step 3: basic details form.
 */
export default function CreateListing() {
  usePageTitle('Create a Listing');
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [form, setForm] = useState({ title: '', location: '', price: '', email: '', phone: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  // Only let user list in commercial categories (not Guides)
  const listable = CATEGORY_ORDER.filter((k) => k !== 'guides');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.email) {
      toast.error('Please fill in title and contact email');
      return;
    }
    const category = getCategory(selectedCategory);
    try {
      setSubmitting(true);
      await axios.post(
        `${API_URL}/api/listings/submissions`,
        {
          category_slug: category.slug,
          category_title: category.title,
          subcategory_slug: selectedSub.slug,
          subcategory_name: selectedSub.name,
          title: form.title,
          description: form.description || null,
          location: form.location || null,
          price: form.price || null,
          email: form.email,
          phone: form.phone || null,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
        }
      );
      toast.success('Listing submitted for review. We will be in touch within 24 hours.');
      setTimeout(() => navigate('/'), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Submission failed. Please try again.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24 max-w-[1200px] mx-auto" data-testid="create-listing-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 mb-8">
        <Link to="/" className="hover:text-gold">Home</Link>
        <ChevronRight size={12} />
        <span className="text-gold">List with us</span>
      </div>

      <h1
        className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white mb-4"
        style={{ fontFamily: 'Playfair Display, serif' }}
        data-testid="create-listing-title"
      >
        List your <span className="text-gold">yacht, product or service</span>
      </h1>
      <p className="text-white/60 max-w-2xl mb-12">
        Pick the category that best describes what you&apos;re offering. We&apos;ll guide you through a three-step submission.
      </p>

      {/* Stepper */}
      <Stepper step={step} />

      {step === 1 && (
        <CategorySelector
          categories={listable}
          onSelect={(key) => {
            setSelectedCategory(key);
            setSelectedSub(null);
            setStep(2);
          }}
        />
      )}

      {step === 2 && selectedCategory && (
        <SubcategorySelector
          category={getCategory(selectedCategory)}
          onBack={() => setStep(1)}
          onSelect={(sub) => {
            setSelectedSub(sub);
            setStep(3);
          }}
        />
      )}

      {step === 3 && selectedCategory && selectedSub && (
        <DetailsForm
          category={getCategory(selectedCategory)}
          sub={selectedSub}
          form={form}
          setForm={setForm}
          submitting={submitting}
          onBack={() => setStep(2)}
          onSubmit={submit}
        />
      )}
    </div>
  );
}

function Stepper({ step }) {
  const steps = ['Category', 'Subcategory', 'Details'];
  return (
    <div className="flex items-center gap-4 mb-12" data-testid="create-listing-stepper">
      {steps.map((label, idx) => {
        const n = idx + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={label} className="flex items-center gap-3">
            <div
              className={`w-9 h-9 border flex items-center justify-center text-xs transition-colors ${
                active
                  ? 'border-gold text-gold'
                  : done
                  ? 'border-gold/60 bg-gold text-[#0A192F]'
                  : 'border-white/20 text-white/40'
              }`}
            >
              {done ? <Check size={14} /> : n}
            </div>
            <span
              className={`text-xs uppercase tracking-[0.25em] ${
                active ? 'text-gold' : done ? 'text-white/80' : 'text-white/40'
              }`}
            >
              {label}
            </span>
            {idx < steps.length - 1 && <div className="w-10 h-px bg-white/10" />}
          </div>
        );
      })}
    </div>
  );
}

function CategorySelector({ categories, onSelect }) {
  return (
    <div>
      <h2
        className="text-2xl font-medium text-white mb-6"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        What are you listing?
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="category-selector">
        {categories.map((key) => {
          const cat = CATEGORIES[key];
          const Icon = getIcon(cat.icon);
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelect(key)}
              className="group relative overflow-hidden text-left h-48 glass-light border border-white/5 hover:border-gold/60 transition-all duration-500"
              data-testid={`create-category-${key}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
                style={{ backgroundImage: `url('${cat.hero}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#050C16] via-[#050C16]/70 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-6">
                <span className="w-11 h-11 border border-gold/50 flex items-center justify-center text-gold">
                  <Icon size={18} />
                </span>
                <div>
                  <h3
                    className="text-2xl font-medium text-white mb-1 group-hover:text-gold transition-colors"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {cat.title}
                  </h3>
                  <p className="text-xs text-white/60 max-w-xs">{cat.tagline}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SubcategorySelector({ category, onBack, onSelect }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2
          className="text-2xl font-medium text-white"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Choose a subcategory within <span className="text-gold">{category.title}</span>
        </h2>
        <Button variant="ghost" onClick={onBack} className="text-white/60 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="create-back-1">
          ← Change category
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="subcategory-selector">
        {category.subcategories.map((sub) => {
          const Icon = getIcon(sub.icon);
          return (
            <button
              key={sub.slug}
              type="button"
              onClick={() => onSelect(sub)}
              className="glass-light border border-white/5 hover:border-gold/60 p-5 text-left flex gap-4 items-start transition-colors"
              data-testid={`create-sub-${sub.slug.replace(/\//g, '-')}`}
            >
              <span className="w-10 h-10 border border-white/15 flex items-center justify-center text-white/70 group-hover:text-gold flex-shrink-0">
                <Icon size={16} />
              </span>
              <span>
                <span className="block text-white text-sm font-medium mb-1">{sub.name}</span>
                {sub.description && (
                  <span className="block text-xs text-white/50 leading-snug">{sub.description}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DetailsForm({ category, sub, form, setForm, submitting, onBack, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="glass-light p-8 border border-white/5" data-testid="create-details-form">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-gold">{category.title}</p>
          <h2
            className="text-2xl font-medium text-white"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {sub.name}
          </h2>
        </div>
        <Button variant="ghost" type="button" onClick={onBack} className="text-white/60 hover:text-gold text-xs uppercase tracking-[0.2em]" data-testid="create-back-2">
          ← Change subcategory
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LabeledInput
          label="Listing title"
          placeholder="e.g. 2019 Sunseeker 76 Yacht"
          value={form.title}
          onChange={(v) => setForm({ ...form, title: v })}
          testId="create-title"
          required
        />
        <LabeledInput
          label="Location"
          placeholder="e.g. Phuket, Thailand"
          value={form.location}
          onChange={(v) => setForm({ ...form, location: v })}
          testId="create-location"
        />
        <LabeledInput
          label="Asking price / rate"
          placeholder="e.g. $2,400,000 or $12,000/week"
          value={form.price}
          onChange={(v) => setForm({ ...form, price: v })}
          testId="create-price"
        />
        <LabeledInput
          label="Contact email"
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={(v) => setForm({ ...form, email: v })}
          testId="create-email"
          required
        />
        <LabeledInput
          label="Phone (optional)"
          placeholder="+66 89 123 4567"
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })}
          testId="create-phone"
        />
        <div className="md:col-span-2">
          <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
            Description
          </label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Briefly describe your listing, specs, condition, availability..."
            className="bg-transparent border-white/20 text-white placeholder:text-white/40 min-h-[140px]"
            data-testid="create-description"
          />
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
        <p className="text-xs text-white/50 max-w-md">
          Our team reviews every submission to protect listing quality. Expect a response within 24 hours.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-gold text-[#0A192F] hover:bg-[#E5C55D] px-6 disabled:opacity-60"
          data-testid="create-submit"
        >
          {submitting ? 'Submitting…' : 'Submit listing'} <ArrowRight size={14} className="ml-2" />
        </Button>
      </div>
    </form>
  );
}

function LabeledInput({ label, value, onChange, placeholder, type = 'text', required, testId }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
        {label}{required ? ' *' : ''}
      </span>
      <Input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-white/20 text-white placeholder:text-white/40"
        data-testid={testId}
      />
    </label>
  );
}
